import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Spinner } from 'components/ui/spinner';
import { Question } from '../../types';
import { media } from '../../services/api';

interface PDFQuestionProps {
  question: Question;
  onValidationChange?: (isValid: boolean) => void;
}

export const PDFQuestion: React.FC<PDFQuestionProps> = ({ question, onValidationChange, }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const metadataPdfUrl = question.metadata?.pdf_url ?? null;

  const pdfStorageKey = useMemo(() => {
    if (question.media?.media_type === 'pdf') {
      return question.media.storage_key ?? null;
    }
    return null;
  }, [question.media?.media_type, question.media?.storage_key]);

  useEffect(() => {
    onValidationChange?.(false);
  }, [onValidationChange, question.id]);


  const loadPdf = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setPdfUrl(null);
    onValidationChange?.(true); // Validate always for now

    if (pdfStorageKey) {
      try {
        const resolvedUrl = await media.getSignedUrl(pdfStorageKey);
        setPdfUrl(resolvedUrl);
        onValidationChange?.(true);
      } catch (err) {
        console.error('Failed to load PDF', err);
        setError('Unable to load PDF. Please try again later.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (metadataPdfUrl) {
      setPdfUrl(metadataPdfUrl);
      onValidationChange?.(true);
      setIsLoading(false);
      return;
    }

    setError('PDF URL is missing.');
    setIsLoading(false);
  }, [pdfStorageKey, metadataPdfUrl, onValidationChange]);

  useEffect(() => {
    loadPdf();
  }, [loadPdf]);

  return (
    <div className="w-full max-w-[28.125rem]">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex h-52 items-center justify-center">
          <Spinner />
        </div>
      )}

      {!isLoading && pdfUrl && (
        <embed
          src={pdfUrl}
          type="application/pdf"
          className="h-[65dvh] w-full rounded-2xl sm:h-[75dvh] md:h-[90vh]"
        />
      )}
    </div>
  );
};
